import useDiscoveryFetch from './useDiscoveryFetch';
import { useState, useEffect, useContext } from 'react';
import api from '../../api/axiosInstance';
import { useParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Image,
    Spinner,
    Alert,
    Table,
    Button,
    OverlayTrigger,
    Tooltip,
    Badge,
    Form,
    Modal,
    Dropdown,
} from 'react-bootstrap';
import Pagination from 'react-bootstrap/Pagination';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { UserContext } from '../../UserContext';
import { isAdminType } from '../../utils/roleUtils';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import NavTabs from '../../NavTabs';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import icNoInsights from '../../assets/img/ic-empty-box.svg';
import icAdd from '../../assets/img/ic-add-circle.svg';
import icDelete from '../../assets/img/ic-delete.svg';
import icEdit from '../../assets/img/ic-edit.svg';
import icApproved from '../../assets/img/ic-check-circle.svg';
import icSuccess from '../../assets/img/ic-celebration-circle.svg';
import icImg from '../../assets/img/ic-img.svg';
import { useMediaQuery } from 'react-responsive';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';
import MobileDiscoveryCommentCard from './MobileDiscoveryCommentCard';
import MobileCommentDetail from './MobileCommentDetail';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import useMobileLoadMore from './useMobileLoadMore';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import './cityDetail/CityCommentsMobile.css';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const applySort = (arr, { by, dir }) => {
    if (!by) return arr;
    const mul = dir === 'desc' ? -1 : 1;
    return [...arr].sort((a, b) => {
        const pick = (key, obj) => {
            switch (key) {
                case 'commentClass': return (obj.commentClass || '').toLowerCase();
                case 'user':         return (`${obj.user?.firstName || ''} ${obj.user?.familyName || ''}`).trim().toLowerCase();
                case 'email':        return (obj.user?.email || '').toLowerCase();
                case 'price':        return parseFloat(obj.pricePerMonth) || 0;
                case 'approved':     return obj.approvedReview ? 1 : 0;
                default:             return '';
            }
        };
        const va = pick(by, a);
        const vb = pick(by, b);
        if (va < vb) return -1 * mul;
        if (va > vb) return  1 * mul;
        return 0;
    });
};

const normalizeUrl = (raw) => {
    if (!raw) return null;
    try {
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(raw) ? raw : `https://${raw}`;
        const u = new URL(withScheme);
        return ['http:', 'https:'].includes(u.protocol) ? u.toString() : null;
    } catch {
        return null;
    }
};

const DiscoveryCityViewComments = () => {
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });
    const CommentFormFooter = isMobile ? 'div' : Modal.Footer;
    const { id, cityId } = useParams();
    const actualCityId = cityId || id;
    const countryCode = cityId ? id : null;
    const { user } = useContext(UserContext);
    const isAdmin = user && isAdminType(user.role);

    const [cityData, setCityData] = useState(null);
    const [comments, setComments] = useState([]);
    const [filteredComments, setFilteredComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [sort, setSort] = useState({ by: null, dir: 'asc' });
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const safeUrlHref = normalizeUrl(cityData?.url);

    const commentDefaults = {
        commentClass: '',
        status: '',
        pricePerMonth: '',
        currency: 'CHF',
        fromDate: '',
        toDate: '',
        photos: [],
        existingPhotoUrls: [],
        comment: '',
    };
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [commentForm, setCommentForm] = useState(commentDefaults);
    const [commentSaving, setCommentSaving] = useState(false);
    const [commentError, setCommentError] = useState('');
    const [editingCommentId, setEditingCommentId] = useState(null);

    const [selectedComment, setSelectedComment] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusModalTitle, setStatusModalTitle] = useState('');
    const [statusModalMessage, setStatusModalMessage] = useState('');
    const [selectedPhotoIdx, setSelectedPhotoIdx] = useState(0);
    const [displayPhotoIdx, setDisplayPhotoIdx] = useState(0);
    const [photoFading, setPhotoFading] = useState(false);

    useEffect(() => { setSelectedPhotoIdx(0); setDisplayPhotoIdx(0); }, [selectedComment]);

    useEffect(() => {
        if (selectedPhotoIdx === displayPhotoIdx) return;
        setPhotoFading(true);
        const t = setTimeout(() => {
            setDisplayPhotoIdx(selectedPhotoIdx);
            setPhotoFading(false);
        }, 200);
        return () => clearTimeout(t);
    }, [selectedPhotoIdx]);

    useDiscoveryFetch(({ signal, isCurrent, isReconnect }) => {
        const fetchData = async () => {
            try {
                if (!isReconnect || !cityData) setLoading(true);
                setError('');
                const response = await api.get(`/api/discovery/cities/${actualCityId}/comments`, { signal });
                if (!isCurrent()) return;
                const data = response.data || {};
                setCityData(data);
                const list = (Array.isArray(data.comments) ? data.comments : []).map(c => ({
                    ...c,
                    country: data.country?.id || '',
                    city: data.name || '',
                }));
                setComments(list);
                setFilteredComments(list);
                if (!isReconnect) setCurrentPage(1);
            } catch (err) {
                if (!isCurrent()) return;
                console.error('Error fetching city comments:', err);
                setError('An error occurred while fetching city comments.');
            } finally {
                if (!isCurrent()) return;
                setLoading(false);
            }
        };

        fetchData();
    }, [actualCityId]);

    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        const next = comments.filter((c) => {
            if (selectedCountry && c.country !== selectedCountry) return false;
            if (selectedCity && c.city !== selectedCity) return false;
            if (!q) return true;
            return [c.commentClass, c.user?.firstName, c.user?.familyName, c.user?.email, c.comment]
                .map(v => String(v || '').toLowerCase())
                .some(s => s.includes(q));
        });
        setFilteredComments(applySort(next, sort));
    }, [searchQuery, selectedCountry, selectedCity, comments]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedCountry, selectedCity]);

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        try {
            setCommentSaving(true);
            setCommentError('');
            const formData = new FormData();
            formData.append('commentClass', commentForm.commentClass);
            formData.append('status', commentForm.status);
            formData.append('pricePerMonth', commentForm.pricePerMonth);
            formData.append('currency', commentForm.currency);
            formData.append('fromDate', commentForm.fromDate);
            formData.append('toDate', commentForm.toDate);
            formData.append('comment', commentForm.comment);
            commentForm.photos.forEach(p => formData.append('photos', p));

            let res;
            if (editingCommentId) {
                formData.append('keepPhotos', JSON.stringify(commentForm.existingPhotoUrls));
                res = await api.patch(`/api/discovery/cities/${actualCityId}/comments/${editingCommentId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const updated = { ...res.data, country: cityData?.country?.id || '', city: cityData?.name || '' };
                setComments(prev => prev.map(c => (c._id || c.id) === editingCommentId ? updated : c));
                setCurrentPage(1);
                if (selectedComment && (selectedComment._id || selectedComment.id) === editingCommentId) {
                    setSelectedComment(updated);
                }
                setStatusModalTitle('Comment updated');
                setStatusModalMessage('The comment was successfully updated.');
            } else {
                res = await api.post(`/api/discovery/cities/${actualCityId}/comments`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const newComment = { ...res.data, country: cityData?.country?.id || '', city: cityData?.name || '' };
                setComments(prev => [newComment, ...prev]);
                setCurrentPage(1);
                setStatusModalTitle('New comment successfully created');
                setStatusModalMessage('Your comment was successfully submitted.');
            }

            setShowCommentModal(false);
            setCommentForm(commentDefaults);
            setEditingCommentId(null);
            setShowStatusModal(true);
        } catch (err) {
            setCommentError(err.response?.data?.msg || 'Failed to save comment.');
        } finally {
            setCommentSaving(false);
        }
    };

    const handleDeleteComment = async () => {
        if (!commentToDelete) return;
        try {
            await api.delete(`/api/discovery/cities/${actualCityId}/comments/${commentToDelete}`);
            setComments(prev => prev.filter(c => (c._id || c.id) !== commentToDelete));
            setCurrentPage(1);
            if (selectedComment && (selectedComment._id || selectedComment.id) === commentToDelete) {
                setSelectedComment(null);
            }
        } catch (err) {
            setStatusModalTitle('Error');
            setStatusModalMessage(err.response?.data?.msg || 'Failed to delete comment.');
            setShowStatusModal(true);
        } finally {
            setShowDeleteModal(false);
            setCommentToDelete(null);
        }
    };

    const handleReviewComment = async (commentId, reviewed) => {
        try {
            const res = await api.patch(`/api/discovery/cities/${actualCityId}/comments/${commentId}/review`, { reviewed });
            const updated = { ...res.data, country: cityData?.country?.id || '', city: cityData?.name || '' };
            setComments(prev => prev.map(c => (c._id || c.id) === commentId ? updated : c));
            setCurrentPage(1);
            if (selectedComment && (selectedComment._id || selectedComment.id) === commentId) {
                setSelectedComment(updated);
            }
            setStatusModalTitle(reviewed ? 'Marked as Reviewed' : 'Marked as Not Reviewed');
            setStatusModalMessage(reviewed ? 'The comment has been marked as reviewed.' : 'The comment has been marked as not reviewed.');
            setShowStatusModal(true);
        } catch (err) {
            setStatusModalTitle('Error');
            setStatusModalMessage(err.response?.data?.msg || 'Failed to update review status.');
            setShowStatusModal(true);
        }
    };

    const toggleSort = (by) => {
        setSort(prev => {
            const dir = prev.by === by ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'asc';
            const next = { by, dir };
            setFilteredComments(applySort(filteredComments, next));
            return next;
        });
    };

    const SortIcon = ({ col }) => (
        <span>
            <FaCaretUp className={sort.by === col && sort.dir === 'asc' ? 'text-primary' : ''} />
            <FaCaretDown className={sort.by === col && sort.dir === 'desc' ? 'text-primary' : ''} />
        </span>
    );

    const totalPages = Math.ceil(filteredComments.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentComments = filteredComments.slice(indexOfFirst, indexOfLast);
    const mobileComments = useMobileLoadMore({
        items: filteredComments,
        filterKey: JSON.stringify([actualCityId, countryCode, searchQuery, selectedCountry, selectedCity, sort]),
        storageKey: 'city-comments-mobile',
        isMobile,
    });

    const basePath = countryCode
        ? `/discovery/countries/${countryCode}/cities/${actualCityId}`
        : `/discovery/cities/${actualCityId}`;
    const discoveryCityNavItems = [
        { to: basePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${basePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${basePath}/companies`] },
        { to: `${basePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${basePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${basePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${basePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext({
        pageType: 'discovery-city',
        discoveryType: 'cities',
        cityId: actualCityId,
        cityName: cityData?.name || cityData?.cityName || '',
        countryName: cityData?.country?.name || '',
        activeTab: 'Comments',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'cities',
            selectedCity: cityData?.name || cityData?.cityName || '',
            selectedCountry: cityData?.country?.name || '',
        }),
    });

    if (loading) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col className="py-5 text-center">
                                <Spinner animation="border" />
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="danger" className="mt-4">{error}</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    if (!cityData) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Alert variant="warning" className="mt-4">City not found.</Alert>
                            </Col>
                        </Row>
                    </Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityData?.name} subPage="Comments" fromCountry={countryCode} countryName={cityData?.country?.name} />
                        </Col>
                    </Row>

                    {isMobile ? (
                        <Row className="g-0 country-detail-mobile city-detail-mobile">
                            <Col className="p-0">
                                <CityDetailHero cityName={cityData.name} url={safeUrlHref ? { href: safeUrlHref, label: cityData.url } : null} countryName={cityData.country?.name} />
                                <CityDetailMobileTabs items={discoveryCityNavItems} />
                                <Card className="country-detail-content-card mb-4"><Card.Body>
                                    <Card className="company-detail-section mb-4 city-comments-section"><Card.Body>
                                        <h3 className="company-detail-section-title">Comments</h3>
                                        <hr />
                                        <Button variant="primary" className="mobile-comment-add" onClick={() => { setCommentForm(commentDefaults); setEditingCommentId(null); setCommentError(''); setShowCommentModal(true); }}>Add new comment</Button>
                                        {mobileComments.totalCount ? <>
                                            <div className="mobile-comment-list">{mobileComments.mobileItems.map(c => <MobileDiscoveryCommentCard key={c._id || c.id} comment={c} onOpen={setSelectedComment} />)}</div>
                                            <MobileLoadMoreFooter visibleCount={mobileComments.visibleCount} totalCount={mobileComments.totalCount} itemLabel="comments" onLoadMore={mobileComments.loadMore} isLoading={mobileComments.isLoadingMore} hasMore={mobileComments.hasMore} />
                                        </> : <DiscoveryEmptyState icon={icNoInsights} title={searchQuery ? 'No matching comments found' : 'No comments found'} />}
                                    </Card.Body></Card>
                                </Card.Body></Card>
                            </Col>
                        </Row>
                    ) : (
                    <Row>
                        <Col>
                            <Card className="mb-4">
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <Row>
                                                <Col>
                                                    <Card className="card-premium">
                                                        <Card.Body>
                                                            <Row className="align-items-center">
                                                                <Col>
                                                                    <h3 className="text-start">City</h3>
                                                                    <hr />
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-4">
                                                                <Col md={8} className="d-flex">
                                                                    <div>
                                                                        <h1>{cityData.name || '—'}</h1>
                                                                        <p>
                                                                            {safeUrlHref ? (
                                                                                <a href={safeUrlHref} target="_blank" rel="noopener noreferrer">
                                                                                    {cityData.url}
                                                                                </a>
                                                                            ) : cityData.url ? cityData.url : '—'}
                                                                            {cityData.country?.name && (
                                                                                <>
                                                                                    <span className="dot"></span>
                                                                                    Country: <strong>{cityData.country.name}</strong>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </Card.Body>
                                                    </Card>
                                                </Col>
                                            </Row>

                                            <Row className="mb-5">
                                                <Col>
                                                    <NavTabs items={discoveryCityNavItems} />
                                                </Col>
                                            </Row>

                                            <Card className="mb-4">
                                                <Card.Body>
                                                    <Row className="align-items-center">
                                                        <Col>
                                                            <h3 className="mb-0">Comments</h3>
                                                        </Col>
                                                        <Col className="text-end">
                                                            <Button variant="primary" size="sm" className="ms-2" onClick={() => setShowCommentModal(true)}>Add new comment</Button>
                                                        </Col>
                                                    </Row>
                                                    <hr className="pt-4" />

                                                    {/* <DiscoveryFilterBar
                                                        variant="comments"
                                                        searchQuery={searchQuery}
                                                        setSearchQuery={setSearchQuery}
                                                        selectedCountry={selectedCountry}
                                                        setSelectedCountry={setSelectedCountry}
                                                        selectedCity={selectedCity}
                                                        setSelectedCity={setSelectedCity}
                                                        items={comments}
                                                    /> */}

                                                    {filteredComments.length === 0 ? (
                                                        <Row>
                                                            <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                                                <Image src={icNoInsights} alt="No Comments" className="mb-4" />
                                                                <h2 className="mb-3">No comments yet.</h2>
                                                                <p className="mb-4">Comments for this city will appear here.</p>
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <>
                                                            <Row>
                                                                <Col>
                                                                    <div className="table-responsive list">
                                                                        <Table>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Photos</th>
                                                                                    <th onClick={() => toggleSort('commentClass')} style={{ cursor: 'pointer' }}>
                                                                                        <div>Comment class <SortIcon col="commentClass" /></div>
                                                                                    </th>
                                                                                    <th>Status</th>
                                                                                    <th onClick={() => toggleSort('user')} style={{ cursor: 'pointer' }}>
                                                                                        <div>User <SortIcon col="user" /></div>
                                                                                    </th>
                                                                                    {/* Commenter emails are for moderation: platform admins only */}
                                                                                    {isAdmin && (
                                                                                        <th onClick={() => toggleSort('email')} style={{ cursor: 'pointer' }}>
                                                                                            <div>Email <SortIcon col="email" /></div>
                                                                                        </th>
                                                                                    )}
                                                                                    <th>Comment</th>
                                                                                    <th onClick={() => toggleSort('approved')} style={{ cursor: 'pointer' }}>
                                                                                        <div>Aproadify reviewed <SortIcon col="approved" /></div>
                                                                                    </th>
                                                                                    <th onClick={() => toggleSort('price')} style={{ cursor: 'pointer' }}>
                                                                                        <div>Price per month <SortIcon col="price" /></div>
                                                                                    </th>
                                                                                    {isAdmin && (
                                                                                        <th></th>
                                                                                    )}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {currentComments.map((c) => {
                                                                                    const userName = c.user ? `${c.user.firstName || ''} ${c.user.familyName || ''}`.trim() || '—' : '—';
                                                                                    const userEmail = c.user?.email || '—';
                                                                                    const userAvatar = resolveMediaUrl(c.user?.profileImage) || null;
                                                                                    const commentId = c._id || c.id;

                                                                                    const commentText = c.comment || c.text || '';
                                                                                    const isLong = commentText.length > 30;
                                                                                    const preview = isLong ? `${commentText.slice(0, 30)}…` : commentText || '—';

                                                                                    return (
                                                                                        <tr key={c._id || c.id} onClick={() => setSelectedComment(c)} style={{ cursor: 'pointer' }}>
                                                                                            <td>
                                                                                                {c.photos?.[0] ? (
                                                                                                    <Image
                                                                                                        src={c.photos[0]}
                                                                                                        alt="comment photo"
                                                                                                        width={56}
                                                                                                        height={32}
                                                                                                        style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #c0d9ee' }}
                                                                                                        className="my-2"
                                                                                                    />
                                                                                                ) : (
                                                                                                    <div className="my-2" style={{ width: 56, height: 32, borderRadius: 4, border: '1px solid #c0d9ee', background: '#eef5fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                                        <img src={icImg} alt="No photo" width={20} height={20} />
                                                                                                    </div>
                                                                                                )}
                                                                                            </td>
                                                                                            <td>{c.commentClass || '—'}</td>
                                                                                            <td>
                                                                                                <OverlayTrigger
                                                                                                    placement="top"
                                                                                                    overlay={
                                                                                                        <Tooltip id={`tooltip-status-${c._id}`}>
                                                                                                            {c.status || 'Unknown'}
                                                                                                        </Tooltip>
                                                                                                    }
                                                                                                >
                                                                                                    <Badge bg={c.status === 'Available' ? 'active' : 'rejected'}>
                                                                                                        &nbsp;
                                                                                                    </Badge>
                                                                                                </OverlayTrigger>
                                                                                            </td>
                                                                                            <td>
                                                                                                <div className="d-flex align-items-center gap-2">
                                                                                                    {userAvatar && (
                                                                                                        <Image
                                                                                                            src={userAvatar}
                                                                                                            style={{ width: '3.2rem', height: '3.2rem', borderRadius: '100%', objectFit: 'cover', flexShrink: 0, border: '1px solid #c0d9ee' }}
                                                                                                        />
                                                                                                    )}
                                                                                                    {userName}
                                                                                                </div>
                                                                                            </td>
                                                                                            {isAdmin && <td>{userEmail}</td>}
                                                                                            <td>
                                                                                                {isLong ? (
                                                                                                    <OverlayTrigger
                                                                                                        placement="top"
                                                                                                        overlay={
                                                                                                            <Tooltip style={{ maxWidth: 300 }}>
                                                                                                                {commentText}
                                                                                                            </Tooltip>
                                                                                                        }
                                                                                                    >
                                                                                                        <span style={{ cursor: 'pointer' }}>{preview}</span>
                                                                                                    </OverlayTrigger>
                                                                                                ) : preview}
                                                                                            </td>
                                                                                            <td>{c.approvedReview === true ? 'Yes' : c.approvedReview === false ? 'No' : 'No'}</td>
                                                                                            <td>
                                                                                                {c.pricePerMonth != null
                                                                                                    ? `${Number(c.pricePerMonth).toLocaleString('de-CH')} ${c.currency || 'CHF'}`
                                                                                                    : '—'}
                                                                                            </td>
                                                                                            {isAdmin && (
                                                                                                <td onClick={e => e.stopPropagation()}>
                                                                                                    <Dropdown
                                                                                                        drop="down"
                                                                                                        align="end"
                                                                                                        show={openDropdownId === commentId}
                                                                                                        onToggle={(isOpen) => {
                                                                                                            setOpenDropdownId(isOpen ? commentId : null);
                                                                                                            if (isOpen) requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
                                                                                                        }}
                                                                                                    >
                                                                                                        <Dropdown.Toggle variant="link" className="text-dark p-0">
                                                                                                            <i className="bi bi-three-dots-vertical"></i>
                                                                                                        </Dropdown.Toggle>
                                                                                                        <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }} style={{ zIndex: 2000 }}>
                                                                                                            {c.approvedReview ? (
                                                                                                                <Dropdown.Item onClick={() => handleReviewComment(commentId, false)} data-text="Mark Not Reviewed">
                                                                                                                    <img src={icApproved} alt="Mark Not Reviewed" />
                                                                                                                    Mark Not Reviewed
                                                                                                                </Dropdown.Item>
                                                                                                            ) : (
                                                                                                                <Dropdown.Item onClick={() => handleReviewComment(commentId, true)} data-text="Mark Reviewed">
                                                                                                                    <img src={icApproved} alt="Mark Reviewed" />
                                                                                                                    Mark Reviewed
                                                                                                                </Dropdown.Item>
                                                                                                            )}
                                                                                                            <Dropdown.Item data-text="Edit" onClick={() => {
                                                                                                                setEditingCommentId(commentId);
                                                                                                                setCommentForm({
                                                                                                                    commentClass: c.commentClass || '',
                                                                                                                    status: c.status || '',
                                                                                                                    pricePerMonth: c.pricePerMonth ?? '',
                                                                                                                    currency: c.currency || 'CHF',
                                                                                                                    fromDate: c.fromDate ? c.fromDate.slice(0, 10) : '',
                                                                                                                    toDate: c.toDate ? c.toDate.slice(0, 10) : '',
                                                                                                                    photos: [],
                                                                                                                    existingPhotoUrls: Array.isArray(c.photos) ? [...c.photos] : [],
                                                                                                                    comment: c.comment || '',
                                                                                                                });
                                                                                                                setOpenDropdownId(null);
                                                                                                                setShowCommentModal(true);
                                                                                                            }}>
                                                                                                                <img src={icEdit} alt="Edit" />
                                                                                                                Edit
                                                                                                            </Dropdown.Item>
                                                                                                            <Dropdown.Item onClick={() => { setCommentToDelete(commentId); setShowDeleteModal(true); }} data-text="Delete">
                                                                                                                <img src={icDelete} alt="Delete" />
                                                                                                                Delete
                                                                                                            </Dropdown.Item>
                                                                                                        </Dropdown.Menu>
                                                                                                    </Dropdown>
                                                                                                </td>
                                                                                            )}
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="pagination-wrapper">
                                                                <Col className="d-flex align-items-center">
                                                                    <span>Total&nbsp;{filteredComments.length}&nbsp;Comments</span>
                                                                    <Pagination>
                                                                        <Pagination.Prev
                                                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                                            disabled={currentPage === 1}
                                                                        />
                                                                        {[...Array(totalPages).keys()].map(number => (
                                                                            <Pagination.Item
                                                                                key={number + 1}
                                                                                active={number + 1 === currentPage}
                                                                                onClick={() => setCurrentPage(number + 1)}
                                                                            >
                                                                                {number + 1}
                                                                            </Pagination.Item>
                                                                        ))}
                                                                        <Pagination.Next
                                                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                                            disabled={currentPage === totalPages}
                                                                        />
                                                                    </Pagination>
                                                                </Col>
                                                                <Col className="pagination-controls">
                                                                    <div className="pagination-controls-per-page">
                                                                        <Form.Select
                                                                            value={rowsPerPage}
                                                                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                                                            style={{ width: 'auto', marginRight: '10px' }}
                                                                        >
                                                                            <option value="10">10 / page</option>
                                                                            <option value="20">20 / page</option>
                                                                            <option value="40">40 / page</option>
                                                                        </Form.Select>
                                                                    </div>
                                                                    <label className="pagination-controls-goto-label" htmlFor="goto-page-comments">
                                                                        Go to
                                                                    </label>
                                                                    <Form.Control
                                                                        id="goto-page-comments"
                                                                        type="number"
                                                                        className="pagination-controls-goto-input"
                                                                        value={currentPage}
                                                                        onChange={(e) => {
                                                                            const newPage = Number(e.target.value);
                                                                            if (newPage >= 1 && newPage <= totalPages) {
                                                                                setCurrentPage(newPage);
                                                                            }
                                                                        }}
                                                                        onBlur={() => {
                                                                            if (currentPage < 1) setCurrentPage(1);
                                                                            if (currentPage > totalPages) setCurrentPage(totalPages);
                                                                        }}
                                                                        style={{ textAlign: 'center' }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}
                                                </Card.Body>
                                            </Card>

                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    )}
                </Container>
            </main>

            {isMobile && selectedComment && <MobileCommentDetail key={selectedComment._id || selectedComment.id} comment={selectedComment} showEmail={Boolean(isAdmin)} onClose={() => setSelectedComment(null)} />}
            {/* Desktop detail modal */}
            {!isMobile && selectedComment && (() => {
                const c = selectedComment;
                const userName = c.user ? `${c.user.firstName || ''} ${c.user.familyName || ''}`.trim() || '—' : '—';
                const userEmail = c.user?.email || '—';
                const dateRange = [c.fromDate, c.toDate]
                    .filter(Boolean)
                    .map(d => new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }))
                    .join(' - ');

                return (
                    <Modal
                        show
                        onHide={() => setSelectedComment(null)}
                        centered
                        size="lg"
                    >
                        <Modal.Header closeButton />

                        <Modal.Body>
                            {(() => {
                                const photos = Array.isArray(c.photos) && c.photos.length ? c.photos : [];
                                const mainPhoto = photos[displayPhotoIdx];
                                return (
                                    <>
                                        {mainPhoto && (
                                            <Image
                                                src={mainPhoto}
                                                alt="comment photo"
                                                className={`comment-photo-main${photoFading ? ' fading' : ''}`}
                                            />
                                        )}
                                        {photos.length > 1 && (
                                            <div className="comment-photo-strip">
                                                {photos.map((photo, idx) => (
                                                    <Image
                                                        key={idx}
                                                        src={photo}
                                                        alt={`photo ${idx + 1}`}
                                                        className={`comment-photo-thumb${idx === selectedPhotoIdx ? ' active' : ''}`}
                                                        onClick={() => setSelectedPhotoIdx(idx)}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}

                            <div>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h2 className="mb-0">{c.commentClass || '—'}</h2>
                                    {c.pricePerMonth != null && (
                                        <div className="comment-price-badge">
                                            <span className="comment-price-value">
                                                {Number(c.pricePerMonth).toLocaleString('de-CH')} {c.currency || 'CHF'}
                                            </span>
                                            <span className="comment-price-period">/ per month</span>
                                        </div>
                                    )}
                                </div>

                                <div className="comment-detail-meta">
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>{c.status || 'Unknown'}</Tooltip>}
                                    >
                                        <span className={`badge-status bg-${c.status === 'Available' ? 'active' : 'rejected'}`} />
                                    </OverlayTrigger>
                                    <span className={`comment-status ${c.status === 'Available' ? 'status-available' : 'status-unavailable'}`}>
                                        {c.status || '—'}
                                    </span>
                                    {dateRange && (
                                        <>
                                            <span className="dot"></span>
                                            <span className="comment-date">{dateRange}</span>
                                        </>
                                    )}
                                    {userName !== '—' && (
                                        <>
                                            <span className="dot"></span>
                                            <span>{userName}</span>
                                        </>
                                    )}
                                    {isAdmin && userEmail !== '—' && (
                                        <>
                                            <span className="dot"></span>
                                            <span>{userEmail}</span>
                                        </>
                                    )}
                                </div>

                                <p className="comment-detail-description mb-0">{c.comment || '—'}</p>
                            </div>
                        </Modal.Body>

                        <Modal.Footer className="justify-content-between">
                            <span className="font-size-md">
                                Aproadify reviewed:{' '}
                                <span className={c.approvedReview === true ? 'text-success' : 'text-danger'}>
                                    <strong>{c.approvedReview === true ? 'Yes' : 'No'}</strong>
                                </span>
                            </span>
                            <Button variant="primary" onClick={() => setSelectedComment(null)}>
                                Go back to Comments
                            </Button>
                        </Modal.Footer>
                    </Modal>
                );
            })()}

            {/* Delete confirmation modal — pattern from CompanyList */}
            <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setCommentToDelete(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this comment?</Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" className="me-4" onClick={() => { setShowDeleteModal(false); setCommentToDelete(null); }}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteComment}>Delete</Button>
                </Modal.Footer>
            </Modal>

            {/* Status update modal — pattern from CompanyEdit */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h2 className="text-center mb-3">{statusModalTitle}</h2>
                    {!statusModalTitle.toLowerCase().includes('error') && (
                        <Image src={icSuccess} alt="" className="d-block m-auto mb-4" />
                    )}
                    <p className="text-center">{statusModalMessage}</p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="primary" onClick={() => setShowStatusModal(false)}>Go back to Comments</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                className={isMobile ? 'with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen city-comment-mobile-form' : 'with-form'}
                show={showCommentModal}
                onHide={() => { setShowCommentModal(false); setCommentForm(commentDefaults); setCommentError(''); setEditingCommentId(null); }}
                centered={!isMobile}
                fullscreen={isMobile}
            >
                <Form onSubmit={handleCommentSubmit} className={isMobile ? 'discovery-filter-mobile-panel' : undefined}>
                    {isMobile ? (
                        <div className="discovery-filter-mobile-panel__header">
                            <Modal.Title>{editingCommentId ? 'Edit comment' : 'Add new comment'}</Modal.Title>
                            <div className="discovery-filter-mobile-panel__header-actions">
                                <button type="button" className="discovery-filter-mobile-panel__close" aria-label="Close comment form"
                                    onClick={() => { setShowCommentModal(false); setCommentForm(commentDefaults); setCommentError(''); setEditingCommentId(null); }}>&times;</button>
                            </div>
                        </div>
                    ) : (
                    <Modal.Header closeButton>
                        <Modal.Title>{editingCommentId ? 'Edit comment' : 'Add new comment'}</Modal.Title>
                    </Modal.Header>
                    )}

                    <Modal.Body>
                        {commentError && <Alert variant="danger">{commentError}</Alert>}

                        <Row className="mb-3">
                            <Col md={5}>
                                <Form.Group controlId="commentClass">
                                    <Form.Label>Comment class <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={commentForm.commentClass}
                                        onChange={(e) => setCommentForm(f => ({ ...f, commentClass: e.target.value }))}
                                        required
                                    >
                                        <option value="">Please select</option>
                                        <option value="Apartment">Apartment</option>
                                        <option value="House">House</option>
                                        <option value="Studio">Studio</option>
                                        <option value="Room">Room</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="pricePerMonth">
                                    <Form.Label>Price per month <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        placeholder=""
                                        value={commentForm.pricePerMonth}
                                        onChange={(e) => setCommentForm(f => ({ ...f, pricePerMonth: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group controlId="currency">
                                    <Form.Label>Currency <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={commentForm.currency}
                                        onChange={(e) => setCommentForm(f => ({ ...f, currency: e.target.value }))}
                                        required
                                    >
                                        <option value="CHF">CHF</option>
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="GBP">GBP</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={5}>
                                <Form.Group controlId="status">
                                    <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                                    <Form.Select
                                        value={commentForm.status}
                                        onChange={(e) => setCommentForm(f => ({ ...f, status: e.target.value }))}
                                        required
                                    >
                                        <option value="">Please select</option>
                                        <option value="Available">Available</option>
                                        <option value="Not Available">Not Available</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group controlId="fromDate">
                                    <Form.Label>From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={commentForm.fromDate}
                                        onChange={(e) => setCommentForm(f => ({ ...f, fromDate: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="toDate">
                                    <Form.Label>To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={commentForm.toDate}
                                        onChange={(e) => setCommentForm(f => ({ ...f, toDate: e.target.value }))}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col>
                                <Form.Group controlId="photos">
                                    <Form.Label>Upload photos <span className="text-danger">*</span></Form.Label>
                                    {commentForm.existingPhotoUrls.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2 mb-2">
                                            {commentForm.existingPhotoUrls.map((url, idx) => (
                                                <div key={idx} style={{ position: 'relative' }}>
                                                    <Image
                                                        src={url}
                                                        width={64}
                                                        height={64}
                                                        style={{ objectFit: 'cover', borderRadius: 6 }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setCommentForm(f => ({ ...f, existingPhotoUrls: f.existingPhotoUrls.filter((_, i) => i !== idx) }))}
                                                        style={{
                                                            position: 'absolute', top: -6, right: -6,
                                                            width: 18, height: 18, borderRadius: '50%',
                                                            background: '#dc3545', border: 'none', color: '#fff',
                                                            fontSize: 11, lineHeight: 1, cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}
                                                    >×</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {(commentForm.photos.length + commentForm.existingPhotoUrls.length) < 6 && (
                                        <div
                                            className="custom-upload-box mb-1"
                                            onClick={() => document.getElementById('commentPhotoInput').click()}
                                        >
                                            <Image className="upload-icon" src={icAdd} alt="Upload photo" />
                                            <span className="upload-text">
                                                Choose file {(commentForm.photos.length + commentForm.existingPhotoUrls.length) > 0 && `(${commentForm.photos.length + commentForm.existingPhotoUrls.length}/6)`}
                                            </span>
                                        </div>
                                    )}
                                    <Form.Control
                                        id="commentPhotoInput"
                                        type="file"
                                        accept=".png,.jpg,.jpeg"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const added = Array.from(e.target.files);
                                            setCommentForm(f => {
                                                const remaining = 6 - f.existingPhotoUrls.length;
                                                return { ...f, photos: [...f.photos, ...added].slice(0, remaining) };
                                            });
                                            e.target.value = '';
                                        }}
                                    />
                                    <p className="info">File format: PNG, JPG. Max 5MB each. Up to 6 photos.</p>
                                    {commentForm.photos.length > 0 && (() => {
                                        const MAX = 5 * 1024 * 1024;
                                        const hasOversized = commentForm.photos.some(p => p.size > MAX);
                                        return (
                                            <>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {commentForm.photos.map((photo, idx) => {
                                                        const tooLarge = photo.size > MAX;
                                                        return (
                                                            <div key={idx} style={{ position: 'relative' }}>
                                                                <Image
                                                                    src={URL.createObjectURL(photo)}
                                                                    width={64}
                                                                    height={64}
                                                                    style={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: 6,
                                                                        border: tooLarge ? '2px solid #dc3545' : 'none',
                                                                        opacity: tooLarge ? 0.6 : 1,
                                                                    }}
                                                                />
                                                                {tooLarge && (
                                                                    <div style={{
                                                                        position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                        background: 'rgba(220,53,69,0.85)',
                                                                        color: '#fff', fontSize: 9, textAlign: 'center',
                                                                        borderRadius: '0 0 6px 6px', padding: '1px 0',
                                                                        lineHeight: 1.4,
                                                                    }}>
                                                                        Too large
                                                                    </div>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setCommentForm(f => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }))}
                                                                    style={{
                                                                        position: 'absolute', top: -6, right: -6,
                                                                        width: 18, height: 18, borderRadius: '50%',
                                                                        background: '#dc3545', border: 'none', color: '#fff',
                                                                        fontSize: 11, lineHeight: 1, cursor: 'pointer',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}
                                                                >×</button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {hasOversized && (
                                                    <p className="text-danger mt-2 mb-0" style={{ fontSize: '1.2rem' }}>
                                                        Some photos exceed 5MB. Please remove them before saving.
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group controlId="commentText">
                                    <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={commentForm.comment}
                                        onChange={(e) => setCommentForm(f => ({ ...f, comment: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>

                    <CommentFormFooter className={isMobile ? 'discovery-filter-mobile-panel__footer' : undefined}>
                        <Button
                            variant="primary"
                            className={isMobile ? 'discovery-filter-mobile-panel__apply' : undefined}
                            type="submit"
                            disabled={commentSaving || commentForm.photos.some(p => p.size > 5 * 1024 * 1024)}
                        >
                            {commentSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </CommentFormFooter>
                </Form>
            </Modal>
        </>
    );
};

export default DiscoveryCityViewComments;
